<template>
    <div class="row condition-row">
        <template v-if="!isLoading">
            <div class="col-4">
                <v-select
                    class="form-control"

                    v-model="fieldModel"
                    :options="selectableFieldTypes"
                    :disabled="!canWrite"
                    :clearable="false"

                    searchable
                    filterable
                />

                <required-error-message v-if="$v.field.$dirty && $v.field.$invalid" />
            </div>

            <div class="col-6">
                <v-select
                    class="form-control"

                    v-model="valueModel"
                    :options="selectableFieldValues"
                    :disabled="!fieldModel || !canWrite"
                    :clearable="false"

                    :multiple="isMultiDropDown"
                    :closeOnSelect="!isMultiDropDown"

                    searchable
                    filterable
                />

                <required-error-message v-if="$v.value.$dirty && $v.value.$invalid" />
            </div>

            <div class="col-2">
                <button v-if="canWrite"
                        class="btn btn-outline-danger"
                        @click="$emit('deleteCondition')"
                >
                    <i class="far fa-trash-alt"></i>
                </button>
            </div>
        </template>
        <template v-else>
            <div class="col-12">
                <loader />
            </div>
        </template>
    </div>
</template>

<script>
import api from "@modules/JourneyBuilder/api";
import {conditionValidations} from './validations';
import {errorGrowl} from '@core/Util/growler';

import {validationMixin} from 'vuelidate';

import VSelect from 'vue-select';
import RequiredErrorMessage from "@core/Components/Errors/RequiredErrorMessage";
import Loader from '@modules/JourneyBuilder/Components/Elements/Loader';


export default {
    name: "BrickFilterCondition",
    
    components: {
        VSelect,
        RequiredErrorMessage,
        Loader
    },
    
    mixins: [validationMixin],

    validations: conditionValidations,

    props: {
        field: {
            type: String,
            required: false //Allow null
        },
        
        value: {
            type: [String, Array],
            required: false //Allow null
        },
        
        canWrite: {
            type: Boolean,
            required: false,
            default: false
        },
        
        // provides a way to filter (only display these) certain categories
        includedCategoryFilters: {
            type: Array,
            default: () => []
        },

        // provides a way to filter (not display these) certain categories
        excludedCategoryFilters: {
            type: Array,
            default: () => []
        }
    },
    
    computed: {
        selectableFieldTypes() {
            return Object.keys(this.fieldDefinitions)
                .filter(typeName => (this.includedCategoryFilters.length === 0 || this.includedCategoryFilters.includes(typeName)) &&
                                    (this.excludedCategoryFilters.length === 0 || !this.excludedCategoryFilters.includes(typeName))
                );
        },
        
        selectableFieldValues() {
            let options = this.fieldDefinitions[this.field] && this.fieldDefinitions[this.field].options || [];
            
            //Only display not-yet selected values for multidropdowns
            if(this.isMultiDropDown && Array.isArray(this.value)) {
                options = options.filter(option => !this.value.includes(option.id));
            }
            
            return options;
        },
        
        isMultiDropDown() {
            return this.fieldDefinitions[this.field] && this.fieldDefinitions[this.field].isMultiDropDown || false;
        },
        
        fieldModel: {
            get() {
                return this.field;
            },
            set(value) {
                this.$v.field.$touch();
                
                this.$emit('update:field', value);
            }
        },
        
        valueModel: {
            get() {
                if(!this.fieldDefinitions[this.field]) {
                    return null;
                }
                
                const { options } = this.fieldDefinitions[this.field];
                
                if(!this.isMultiDropDown) {
                    return options.find(option => option.id === this.value);
                }
                
                //Don't crash when no value has been configured yet
                if(!this.value || !Array.isArray(this.value)) {
                    return [];
                }
                
                return options.filter(option => this.value.includes(option.id));
            },
            set(value) {
                this.$v.value.$touch();

                //Account for multi values
                if(Array.isArray(value)) {
                    value = value.map(v => v.id);
                } else {
                    //Don't crash when value is cleared
                    value = value && value.id;
                }
                
                this.$emit('update:value', value);
            }
        }
    },
    
    data() {
        return {
            /**
             * @type {Object.<String, { name: String, isMultiDropDown: Boolean, options: { value: String, label: String }[] }>}
             */
            fieldDefinitions: {},
            
            isLoading: true
        }
    },

    watch: {
        type() {
            //Inform our parent component of a potential change in value
            // noinspection SillyAssignmentJS
            this.valueModel = this.valueModel;
        }
    },
    
    async created() {
        try {
            const remoteFieldDefinitions = await api.getAudienceFilterFieldTypes();
            
            remoteFieldDefinitions.forEach((remoteFieldDefinition, index) => {
                //We currently only support (multi)dropdowns
                if(!remoteFieldDefinition.isDropDown && !remoteFieldDefinition.isMultiDropDown) {
                    return;
                }
                
                if(!this.fieldDefinitions[remoteFieldDefinition.type]) {
                    //Set as a reactive property
                    this.$set(
                        this.fieldDefinitions, 
                        remoteFieldDefinition.type, 
                        {
                            isMultiDropDown: remoteFieldDefinition.isMultiDropDown,
                            options: []
                        }
                    );
                }
                
                //Map to VueSelect compatible format
                this.fieldDefinitions[remoteFieldDefinition.type].options.push({
                    id: remoteFieldDefinition.id,
                    label: remoteFieldDefinition.value
                });
            });

            this.isLoading = false;
        } catch (e) {
            errorGrowl(e);
        }
        
        //In case of a switch between regular dropdown -> multi dropdown, attempt to convert our values
        if(this.value) {
            if(this.isMultiDropDown && !Array.isArray(this.value)) {
                this.$emit('update:value', [this.value]);
            } else if (!this.isMultiDropDown && Array.isArray(this.value)) {
                //A value of undefined is fine, as long as it's not an array anymore
                this.$emit('update:value', this.value[0]);
            }
        }
    },
    
    methods: {
        displayErrors() {
            this.$v.$touch();
        }
    }
}
</script>

<style scoped>
.condition-row {
    margin: 0.6rem 0;
}
</style>